import { useState, useEffect, useRef } from 'react';
import { api, getErrorMessage } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { User, Mail, Phone, CreditCard, Upload, X, Image, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTabs } from '@/context/TabsContext';
import PhoneInput from '@/components/PhoneInput';
import AddressInput from '@/components/AddressInput';

export default function GuestEditTab({ tabId, data, isNew = false }) {
  const { closeTab } = useTabs();
  const { guestId, guest: initialGuest, onRefresh } = data || {};
  
  const [loading, setLoading] = useState(!isNew && !initialGuest);
  const [saving, setSaving] = useState(false);
  const [idTypes, setIdTypes] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    id_type_id: '',
    id_number: '',
    id_proof_image: '',
    address: {
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      pin_code: '',
      country: 'India'
    }
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadData();
  }, [guestId, isNew]);

  const loadData = async () => {
    try {
      // For new guest, only load ID types
      if (isNew) {
        const idTypesRes = await api.get('/id-types');
        setIdTypes(idTypesRes.data.filter(t => t.is_active));
        setLoading(false);
        return;
      }
      
      const [idTypesRes, guestRes] = await Promise.all([
        api.get('/id-types'),
        initialGuest ? Promise.resolve({ data: initialGuest }) : api.get(`/guests/${guestId}`)
      ]);
      
      setIdTypes(idTypesRes.data.filter(t => t.is_active));
      
      const guest = guestRes.data;
      setFormData({
        name: guest.name || '',
        email: guest.email || '',
        phone: guest.phone || '',
        id_type_id: guest.id_type_id || '',
        id_number: guest.id_number || '',
        id_proof_image: guest.id_proof_image || '',
        address: typeof guest.address === 'object' ? guest.address : {
          address_line1: guest.address || '',
          address_line2: '',
          city: '',
          state: '',
          pin_code: '',
          country: 'India'
        }
      });
    } catch (error) {
      toast.error('Failed to load guest data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Guest name is required');
      return;
    }
    if (!formData.phone.trim()) {
      toast.error('Phone number is required');
      return;
    }

    setSaving(true);
    try {
      if (isNew) {
        await api.post('/guests', formData);
        toast.success('Guest added successfully');
      } else {
        await api.put(`/guests/${guestId}`, formData);
        toast.success('Guest updated successfully');
      }
      onRefresh?.();
      closeTab(tabId);
    } catch (error) {
      toast.error(getErrorMessage(error, isNew ? 'Failed to add guest' : 'Failed to update guest'));
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      // Use the correct endpoint for ID document upload
      const response = await api.post('/upload/id-document', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, id_proof_image: response.data.document_url || response.data.url }));
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
        <span className="ml-2 text-gray-600">Loading guest data...</span>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <User className="h-6 w-6 text-orange-600" />
          {isNew ? 'Add New Guest' : 'Edit Guest'}
        </h1>
        <p className="text-gray-500 mt-1">{isNew ? 'Enter guest information' : 'Update guest information'}</p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="flex-1 overflow-auto">
        <div className="max-w-2xl space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader className="py-3 bg-orange-50 border-b">
              <CardTitle className="text-sm font-medium text-orange-700">Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-700">Full Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-700">Phone *</Label>
                  <PhoneInput
                    value={formData.phone}
                    onChange={(value) => setFormData({ ...formData, phone: value })}
                    required
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label className="text-gray-700">Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1"
                />
              </div>
            </CardContent>
          </Card>

          {/* ID Information */}
          <Card>
            <CardHeader className="py-3 bg-blue-50 border-b">
              <CardTitle className="text-sm font-medium text-blue-700">ID Information</CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-700">ID Type</Label>
                  <Select 
                    value={formData.id_type_id} 
                    onValueChange={(value) => setFormData({ ...formData, id_type_id: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select ID Type" />
                    </SelectTrigger>
                    <SelectContent>
                      {idTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-700">ID Number</Label>
                  <Input
                    value={formData.id_number}
                    onChange={(e) => setFormData({ ...formData, id_number: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
              
              {/* ID Proof Image */}
              <div>
                <Label className="text-gray-700">ID Proof Image</Label>
                <div className="mt-2 flex items-center gap-4">
                  {formData.id_proof_image ? (
                    <div className="relative">
                      <img 
                        src={formData.id_proof_image} 
                        alt="ID Proof" 
                        className="h-20 w-32 object-cover rounded border"
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, id_proof_image: '' })}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="h-20 w-32 border-2 border-dashed rounded flex items-center justify-center text-gray-400">
                      <Image className="h-8 w-8" />
                    </div>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload
                  </Button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/*"
                    className="hidden"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Address */}
          <Card>
            <CardHeader className="py-3 bg-emerald-50 border-b">
              <CardTitle className="text-sm font-medium text-emerald-700">Address</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <AddressInput
                value={formData.address}
                onChange={(value) => setFormData({ ...formData, address: value })}
              />
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              disabled={saving}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {saving ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Saving...</>
              ) : (
                <><Save className="h-4 w-4 mr-2" /> {isNew ? 'Add Guest' : 'Save Changes'}</>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => closeTab(tabId)}
            >
              Cancel
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
